import {
  ArrowLeftIcon,
  CheckCircleIcon,
  DownloadSimpleIcon,
  InfoIcon,
} from '@phosphor-icons/react';
import { Elements } from '@stripe/react-stripe-js';
import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, Navigate, useParams } from 'react-router';

import { useConfig } from '@entities/config/api';
import {
  isPaymentPending,
  useCreatePaymentIntent,
  usePaymentPreview,
  usePaymentStatus,
  usePayOrder,
  useSyncPayment,
  type OrderConsents,
} from '@entities/payment';
import { useSearchSession } from '@entities/search-session';
import { KarlDialogue } from '@features/chat';
import { getStripe, stripeAppearance, StripePaymentForm } from '@features/payment';
import { ApiError } from '@shared/api/problem';
import { ROUTES } from '@shared/config/routes';
import { useAppShell } from '@shared/lib/appShell';
import { money } from '@shared/lib/money';
import { percentFromBps } from '@shared/lib/percent';
import {
  Button,
  ButtonLink,
  Checkbox,
  Container,
  NextStepAction,
  Skeleton,
  StateBlock,
} from '@shared/ui';

import styles from './PaymentPage.module.css';

/** Пример скидки для расчёта вознаграждения: 1 000 € в центах (MVP_PRD §35.5). */
const FEE_EXAMPLE_DISCOUNT_CENTS = 100_000;

/**
 * P-01 Оплата (FE-210, MVP_PRD §35, референс `main_screen2`).
 *
 * Экран держит четыре вещи и ни одной сверх:
 *
 * 1. **тариф**: название, крупная цена, состав услуги. Цена приходит с сервера
 *    — хардкод цены в интерфейсе и в локалях запрещён (MVP_TECH_RFC §25);
 * 2. **обязательные тексты рядом с кнопкой** (§35.3), включая условия
 *    вознаграждения по результату с примером на числах (§35.5). Формулировок
 *    «больше платить не придётся» здесь быть не может: после покупки может
 *    возникнуть второй платёж, и умолчание о нём — обман;
 * 3. **пометку демонстрационного режима**. Платёжная форма не имитируется:
 *    ни полей карты, ни логотипов платёжных систем, ни 3-D Secure (§35.2);
 * 4. **чек** после оплаты и переход к следующему шагу.
 *
 * Пустой рабочий набор экран не показывает вовсе: платить не за что (§34.1).
 */
export function PaymentPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'de';
  const { searchId = '' } = useParams();

  const sessionQuery = useSearchSession(searchId || null);
  const preview = usePaymentPreview(searchId || null);
  const payment = usePayOrder(searchId);
  const config = useConfig();

  // Настоящая платёжная форма или прежняя заглушка — решает конфигурация
  // сервера, а не сборка фронта: один и тот же образ обслуживает стенд с
  // заглушкой и прод с настоящим провайдером.
  const provider = config.data?.payments.provider ?? 'stub';
  const stripeEnabled = provider === 'stripe' && Boolean(config.data?.payments.publishable_key);
  const liveMode = config.data?.payments.live_mode ?? false;
  const holdsFunds = config.data?.payments.capture_method === 'manual';

  const intent = useCreatePaymentIntent(searchId);
  const sync = useSyncPayment(searchId);
  const clientSecret = intent.data?.client_secret ?? null;

  // Опрос включается только после подтверждения формы: до него спрашивать не о
  // чем, а после — новость приходит событием и может опередить наш `sync`.
  const [confirmed, setConfirmed] = useState(false);
  const paymentStatus = usePaymentStatus(searchId || null, { watch: confirmed });
  const current = paymentStatus.data ?? null;
  const waitingForResult = confirmed && isPaymentPending(current);

  const stripePromise = useMemo(
    () => (stripeEnabled ? getStripe(config.data?.payments.publishable_key ?? '') : null),
    [stripeEnabled, config.data?.payments.publishable_key],
  );

  // Три согласия § 312j Abs. 3 и § 356 Abs. 5 Nr. 2 BGB. Ни одно не отмечено
  // заранее: заранее отмеченная галочка согласием не является (C-673/17), а
  // норма о досрочном начале требует прямого волеизъявления.
  const [consents, setConsents] = useState<OrderConsents>({
    terms_accepted: false,
    withdrawal_notice_acknowledged: false,
    immediate_start_requested: false,
  });
  const [showConsentErrors, setShowConsentErrors] = useState(false);
  const consentsComplete =
    consents.terms_accepted &&
    consents.withdrawal_notice_acknowledged &&
    consents.immediate_start_requested;

  useAppShell({
    stage: sessionQuery.data?.session.stage ?? null,
    paid: sessionQuery.data?.session.paid ?? false,
    karl: {
      // Диалог живёт в рельсе: уточнить запрос можно и перед оплатой —
      // уточнение бесплатно и пересобирает подбор (§35.4).
      extra: <KarlDialogue searchId={searchId} />,
    },
  });

  if (preview.isPending) {
    return (
      <Container>
        <div className={styles.page} aria-hidden="true">
          <Skeleton height="2rem" width="40%" />
          <Skeleton height="20rem" radius="var(--radius-card)" />
        </div>
      </Container>
    );
  }

  if (preview.isError) {
    // Выключенная оплата и чужой заказ приходят одинаковым `404`, но код в теле
    // разный: экран обязан объяснить причину, а не показать общий сбой.
    const disabled =
      preview.error instanceof ApiError && preview.error.code === 'PAYMENTS_DISABLED';
    return (
      <Container>
        <div className={styles.page}>
          <StateBlock
            tone={disabled ? 'neutral' : 'error'}
            title={disabled ? t('payment.disabled.title') : t('payment.error.title')}
            description={disabled ? t('payment.disabled.text') : t('payment.error.text')}
            action={
              <ButtonLink to={ROUTES.searchResults(searchId)} variant="secondary">
                {t('payment.backToResults')}
              </ButtonLink>
            }
          />
        </div>
      </Container>
    );
  }

  const data = preview.data;

  // Деньги получены **на этом экране**: человек только что нажал кнопку и
  // вправе увидеть результат, а не молчаливый переход. Счёт по § 14 UStG
  // доступен отсюда: личного кабинета в продукте пока нет, а документ обязан
  // быть у покупателя.
  if (current?.paid && confirmed) {
    return (
      <Container>
        <div className={styles.page}>
          <section className={styles.card} aria-labelledby="payment-done">
            <div className={styles.cardBody}>
              <h1 id="payment-done" className={styles.includesTitle}>
                {t('payment.success.title')}
              </h1>
              <p className={styles.priceNote}>
                {t('payment.success.text', {
                  amount: money(current.amount_captured_cents, current.currency, locale),
                  card:
                    current.card_brand && current.card_last4
                      ? `${current.card_brand} •••• ${current.card_last4}`
                      : t('payment.success.cardUnknown'),
                })}
              </p>
              <dl className={styles.orderSummary}>
                <dt>{t('payment.success.net')}</dt>
                <dd className="tabular">
                  {money(current.net_amount_cents, current.currency, locale)}
                </dd>
                <dt>
                  {t('payment.success.vat', { rate: percentFromBps(current.vat_rate_bps, locale) })}
                </dt>
                <dd className="tabular">
                  {money(current.vat_amount_cents, current.currency, locale)}
                </dd>
                <dt>{t('payment.success.invoice')}</dt>
                <dd>{current.invoice_number ?? t('payment.success.invoicePending')}</dd>
              </dl>
              {current.invoice_number ? (
                <a
                  className={styles.invoiceLink}
                  href={`/api/v1/account/invoices/${current.payment_id}/pdf`}
                >
                  <DownloadSimpleIcon size={16} aria-hidden="true" />
                  {t('payment.success.download')}
                </a>
              ) : null}
              <NextStepAction to={ROUTES.searchTop4(searchId)} className={styles.action}>
                {t('payment.success.continue')}
              </NextStepAction>
            </div>
          </section>
        </div>
      </Container>
    );
  }

  // Заказ был оплачен раньше — показывать нечего, работа идёт на четвёрке.
  //
  // `confirmed` исключает тот же экран сразу после оплаты: там человек только
  // что нажал кнопку и обязан увидеть сумму, карту и номер счёта, а не
  // молчаливый переход.
  if (!confirmed && (payment.isSuccess || data.paid)) {
    return <Navigate to={ROUTES.searchTop4(searchId)} replace />;
  }

  if (data.shortlist_size === 0) {
    // Экран оплаты в этом случае не показывается вообще: платить не за что
    // (MVP_PRD §34.1). Пользователю нужны критерии, а не кнопка.
    return (
      <Container>
        <div className={styles.page}>
          <StateBlock
            title={t('payment.empty.title')}
            description={t('payment.empty.text')}
            action={
              <ButtonLink to={ROUTES.searchAssistant(searchId)}>
                {t('payment.empty.action')}
              </ButtonLink>
            }
          />
        </div>
      </Container>
    );
  }

  const price = money(data.amount_cents, data.currency, locale);
  const feeExample = money(
    (FEE_EXAMPLE_DISCOUNT_CENTS * data.success_fee.rate_bps) / 10_000,
    data.currency,
    locale,
  );

  return (
    <Container>
      <div className={styles.page}>
        <Link to={ROUTES.searchResults(searchId)} className={styles.back}>
          <ArrowLeftIcon size={16} weight="bold" aria-hidden="true" />
          {t('payment.backToResults')}
        </Link>

        <section className={styles.card} aria-labelledby="payment-title">
          <header className={styles.cardHead}>
            <p className={styles.package}>{t('payment.package')}</p>
            <h1 id="payment-title" className={`${styles.price} tabular`}>
              {price}
            </h1>
            <p className={styles.priceNote}>{t('payment.priceNote')}</p>
            {/* § 312j Abs. 1 BGB — в начале процесса заказа: принимаемые
                способы оплаты и ограничения поставки. */}
            <p className={styles.priceNote}>{t('payment.paymentMeans')}</p>
          </header>

          <div className={styles.cardBody}>
            <h2 className={styles.includesTitle}>
              {t('payment.includes.title', { count: data.shortlist_size })}
            </h2>
            <ul className={styles.includes}>
              {data.includes.map((code) => (
                <li key={code} className={styles.include}>
                  <CheckCircleIcon size={18} weight="fill" aria-hidden="true" />
                  <span>{t(`payment.includes.item.${code}`)}</span>
                </li>
              ))}
            </ul>

            {/* Обязательные тексты §35.3 — рядом с кнопкой, а не в подвале. */}
            <ul className={styles.terms}>
              <li>{t('payment.terms.oneTime')}</li>
              <li>{t('payment.terms.afterPayment')}</li>
              {data.success_fee.enabled ? (
                <li>
                  {t('payment.terms.successFee', {
                    rate: percentFromBps(data.success_fee.rate_bps, locale),
                    flat: money(data.success_fee.flat_cents, data.currency, locale),
                    price,
                  })}
                </li>
              ) : null}
              <li>{t('payment.terms.notAParty')}</li>
            </ul>

            {data.success_fee.enabled ? (
              <p className={styles.example}>
                {t('payment.terms.successFeeExample', {
                  discount: money(FEE_EXAMPLE_DISCOUNT_CENTS, data.currency, locale),
                  fee: feeExample,
                })}
              </p>
            ) : null}

            {/* Согласия стоят **над** сведениями § 312j Abs. 2 BGB, а не между
                ними и кнопкой: закон требует, чтобы между обязательными
                сведениями и кнопкой не было разделяющих элементов. */}
            <div className={styles.consents}>
              <Checkbox
                checked={consents.terms_accepted}
                error={
                  showConsentErrors && !consents.terms_accepted
                    ? t('payment.consent.required')
                    : undefined
                }
                onChange={(event) => {
                  setConsents((previous) => ({
                    ...previous,
                    terms_accepted: event.target.checked,
                  }));
                }}
              >
                <Trans
                  i18nKey="payment.consent.terms"
                  components={{ terms: <Link to={ROUTES.terms} target="_blank" /> }}
                />
              </Checkbox>

              <Checkbox
                checked={consents.withdrawal_notice_acknowledged}
                error={
                  showConsentErrors && !consents.withdrawal_notice_acknowledged
                    ? t('payment.consent.required')
                    : undefined
                }
                onChange={(event) => {
                  setConsents((previous) => ({
                    ...previous,
                    withdrawal_notice_acknowledged: event.target.checked,
                  }));
                }}
              >
                <Trans
                  i18nKey="payment.consent.withdrawal"
                  components={{ withdrawal: <Link to={ROUTES.withdrawal} target="_blank" /> }}
                />
              </Checkbox>

              {/* § 356 Abs. 5 Nr. 2 BGB. Текст приходит с сервера дословно:
                  доказывать придётся содержание согласия, а не факт клика, и
                  вторая копия формулировки в локали сделала бы это
                  невозможным. */}
              <Checkbox
                checked={consents.immediate_start_requested}
                error={
                  showConsentErrors && !consents.immediate_start_requested
                    ? t('payment.consent.required')
                    : undefined
                }
                onChange={(event) => {
                  setConsents((previous) => ({
                    ...previous,
                    immediate_start_requested: event.target.checked,
                  }));
                }}
              >
                {config.data?.legal.immediate_start_consent.text ??
                  t('payment.consent.immediateStartFallback')}
              </Checkbox>
            </div>

            {/* § 312j Abs. 2 BGB: существенные свойства услуги, общая цена с
                налогом и условия — «unmittelbar bevor der Verbraucher seine
                Bestellung abgibt … in hervorgehobener Weise». Ссылки для этого
                недостаточно, поэтому здесь текст, а не ссылка, и между этим
                блоком и кнопкой нет ничего. */}
            <dl className={styles.orderSummary}>
              <dt>{t('payment.summary.service')}</dt>
              <dd>{t('payment.summary.serviceValue', { count: data.shortlist_size })}</dd>
              <dt>{t('payment.summary.total')}</dt>
              <dd className="tabular">{t('payment.summary.totalValue', { price })}</dd>
              <dt>{t('payment.summary.term')}</dt>
              <dd>{t('payment.summary.termValue')}</dd>
            </dl>

            {stripeEnabled && clientSecret && stripePromise ? (
              // Форма появляется **после** согласий: карту просят только у
              // того, кто уже принял платёжную обязанность (§ 312j Abs. 3 BGB).
              <Elements
                stripe={stripePromise}
                options={{ clientSecret, appearance: stripeAppearance(), locale: locale as 'de' }}
              >
                <StripePaymentForm
                  submitLabel={t('payment.stripe.pay', { price })}
                  pendingLabel={t('payment.pending')}
                  returnUrl={`${window.location.origin}${ROUTES.searchPayment(searchId)}`}
                  onConfirmed={() => {
                    // Ответ формы означает только «браузер дошёл до конца».
                    // Оплачен заказ или нет, решает событие платёжной системы;
                    // `sync` лишь спрашивает сервер раньше, чем оно доедет.
                    setConfirmed(true);
                    sync.mutate();
                  }}
                />
              </Elements>
            ) : (
              <Button
                className={styles.action}
                disabled={!data.payable || payment.isPending || intent.isPending}
                onClick={() => {
                  if (!consentsComplete) {
                    // Фронт — не защита: сервис откажет и сам (`422`). Здесь
                    // задача другая — не отправлять заведомо отказной запрос и
                    // показать, чего не хватает.
                    setShowConsentErrors(true);
                    return;
                  }
                  if (stripeEnabled) {
                    intent.mutate({ ...consents, save_card: false });
                    return;
                  }
                  payment.mutate(consents);
                }}
              >
                {payment.isPending || intent.isPending
                  ? t('payment.pending')
                  : stripeEnabled
                    ? t('payment.stripe.start')
                    : t('payment.submit')}
              </Button>
            )}

            {waitingForResult ? (
              <p className={styles.priceNote} role="status">
                {t('payment.stripe.waiting')}
              </p>
            ) : null}

            {/* Отказ эмитента объясняется кодом, а не общим «что-то пошло не
                так»: человеку нужно знать, пробовать ли другую карту. */}
            {current && !current.paid && current.decline_code ? (
              <p className={styles.error} role="alert">
                {t(`payment.stripe.decline.${current.decline_code}`, {
                  defaultValue: t('payment.stripe.decline.generic'),
                })}
              </p>
            ) : null}

            {payment.isError || intent.isError ? (
              <p className={styles.error} role="alert">
                {t('payment.error.text')}
              </p>
            ) : null}

            {/* Режим назван прямо: «заглушка» и «тестовые карты» — разные вещи,
                и путать их нельзя (§25). */}
            <p className={styles.demo}>
              <InfoIcon size={16} aria-hidden="true" />
              {stripeEnabled
                ? liveMode
                  ? t('payment.stripe.live')
                  : t('payment.stripe.sandbox')
                : t('payment.demo')}
            </p>

            {stripeEnabled && holdsFunds ? (
              // Заморозка — не оплата, и умолчать об этом нельзя: деньги на
              // карте блокируются, а списываются отдельным решением.
              <p className={styles.demo}>
                <InfoIcon size={16} aria-hidden="true" />
                {t('payment.stripe.hold')}
              </p>
            ) : null}
          </div>
        </section>
      </div>
    </Container>
  );
}
